import React, { useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// Configure PDF.js worker to use the local file.
pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

const PDFViewer = ({
  pdfUrl,
  className = '',
  onLoadSuccess,
  onLoadError
}) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const DEFAULT_SCALE = 1.5;
  const MIN_SCALE = 0.5;
  const MAX_SCALE = 5.0;
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rotation, setRotation] = useState(0);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setIsLoading(false);
    setError(null);
    onLoadSuccess?.({ numPages });
  }, [onLoadSuccess]);

  const onDocumentLoadError = useCallback((error) => {
    console.error('PDF loading error:', error);
    setIsLoading(false);
    setError('Failed to load PDF document. Please check the file format and try again.');
    onLoadError?.(error);
  }, [onLoadError]);

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(1, prev - 1));
  };

  const rotateClockwise = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const rotateCounterClockwise = () => {
    setRotation(prev => (prev - 90 + 360) % 360);
  };

  const resetRotation = () => setRotation(0);

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(numPages || 1, prev + 1));
  };

  const goToPage = (page) => {
    const pageNum = parseInt(page);
    if (pageNum >= 1 && pageNum <= (numPages || 1)) {
      setPageNumber(pageNum);
    }
  };

  const zoomIn = () => {
    setScale(prev => Math.min(MAX_SCALE, prev + 0.25));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(MIN_SCALE, prev - 0.25));
  };

  const resetZoom = () => {
    setScale(DEFAULT_SCALE);
  };

  if (error) {
    return (
      <div className={`h-full flex flex-col bg-card ${className}`}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="FileText" size={20} className="text-error" />
            <h3 className="text-sm font-medium text-foreground">PDF Viewer</h3>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-3">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto" />
            <div>
              <h4 className="text-lg font-medium text-foreground">Unable to load PDF</h4>
              <p className="text-sm text-muted-foreground mt-1">{error}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => window.location?.reload()}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full flex flex-col bg-card ${className}`}>
      {/* Header with Controls */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="FileText" size={20} className="text-primary" />
          <h3 className="text-sm font-medium text-foreground">PDF Viewer</h3>
          {isLoading && (
            <Icon name="Loader2" size={16} className="text-muted-foreground animate-spin" />
          )}
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomOut}
            disabled={scale <= MIN_SCALE}
            iconName="ZoomOut"
            title="Zoom out"
          />

          <button
            onClick={resetZoom}
            className="px-2 py-1 text-xs font-mono bg-muted rounded border border-border hover:bg-muted/80 transition-smooth min-w-[3rem] text-center"
            title="Reset zoom"
          >
            {Math.round(scale * 100)}%
          </button>

          <Button
            variant="ghost"
            size="sm"
            onClick={zoomIn}
            disabled={scale >= MAX_SCALE}
            iconName="ZoomIn"
            title="Zoom in"
          />
          <div className="flex items-center space-x-2 border-l border-border pl-2 ml-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={rotateCounterClockwise}
              iconName="RotateCcw"
              title="Rotate counter-clockwise"
            />
            <button
              onClick={resetRotation}
              className="px-2 py-1 text-xs font-mono bg-muted rounded border border-border hover:bg-muted/80 transition-smooth"
              title="Reset rotation"
            >
              {rotation}&deg;
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={rotateClockwise}
              iconName="RotateCw"
              title="Rotate clockwise"
            />
          </div>
        </div>
      </div>

      {/* PDF Content */}
      <div className="flex-1 overflow-auto bg-muted/30 p-4">
        <div className="flex justify-center">
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex items-center justify-center h-96">
                <div className="text-center space-y-3">
                  <Icon name="Loader2" size={32} className="text-primary animate-spin mx-auto" />
                  <p className="text-sm text-muted-foreground">Loading PDF...</p>
                </div>
              </div>
            }
            error={
              <div className="flex items-center justify-center h-96">
                <div className="text-center space-y-3">
                  <Icon name="AlertCircle" size={32} className="text-error mx-auto" />
                  <p className="text-sm text-error">Failed to load PDF</p>
                </div>
              </div>
            }
            className="shadow-moderate"
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              rotate={rotation}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              className="border border-border bg-white"
              loading={
                <div className="flex items-center justify-center h-96 w-full">
                  <Icon name="Loader2" size={24} className="text-primary animate-spin" />
                </div>
              }
            />
          </Document>
        </div>
      </div>

      {/* Navigation Footer */}
      {numPages && (
        <div className="flex items-center justify-between p-4 border-t border-border bg-muted/30">
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              iconName="ChevronLeft"
              title="Previous page"
            />

            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Page</span>
              <input
                type="number"
                min="1"
                max={numPages}
                value={pageNumber}
                onChange={(e) => goToPage(e?.target?.value)}
                className="w-16 px-2 py-1 text-sm text-center border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
              />
              <span className="text-sm text-muted-foreground">of {numPages}</span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              iconName="ChevronRight"
              title="Next page"
            />
          </div>

          <div className="text-xs text-muted-foreground">
            Use mouse to select text • Zoom: {Math.round(scale * 100)}%
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFViewer;
